import { GoogleGenerativeAI } from '@google/generative-ai'
import moment from 'moment'
import { storeData, getStoredData, removeStoredItem } from '@/controllers/BaseController'
import mockInfoItems from '@/dummydata/mockInfoItems'
import { loadConfig } from '@/configLoader'

const API_KEY = 'AIzaSyDd4cSNFiWgg8vvVaeLkQm9fE29Ii91V5g'
const genAI = new GoogleGenerativeAI(API_KEY)
const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' })

// Helper to extract JSON from response
function extractJsonFromText(text) {
  const jsonMatch = text.match(/\[.*\]|\{.*\}/s)
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[0])
    } catch (e) {
      console.error('Failed to parse JSON:', e, 'Text:', text)
    }
  }
  console.warn('No valid JSON found in response:', text)
  return []
}

// Helper to trim description to one sentence
function trimToOneSentence(description) {
  if (!description) return ''
  const firstSentenceEnd = description.indexOf('.')
  if (firstSentenceEnd === -1) return description
  return description.substring(0, firstSentenceEnd + 1).trim()
}

// Generate UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// List of fallback images from multiple public sources
const fallbackImages = [
  // Unsplash
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c',
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3',
  // Pexels
  'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg',
  'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg',
  // Pixabay
  'https://cdn.pixabay.com/photo/2016/11/29/04/19/office-1866151_1280.jpg',
  'https://cdn.pixabay.com/photo/2017/08/01/11/48/people-2563491_1280.jpg',
  // FreePik (public domain images)
  'https://img.freepik.com/free-photo/group-people-working-office_1303-21118.jpg',
  'https://img.freepik.com/free-photo/business-team-working-together-office_1303-9633.jpg',
]

// Function to validate an image URL
async function isValidImageUrl(url) {
  try {
    const response = await fetch(url, { method: 'HEAD' })
    const contentType = response.headers.get('content-type')
    return response.ok && contentType && contentType.startsWith('image/')
  } catch (error) {
    console.error('Error validating image URL:', url, error)
    return false
  }
}

// Function to select a valid fallback image
async function getValidImage(index, providedUrl) {
  if (providedUrl && (await isValidImageUrl(providedUrl))) {
    return providedUrl
  }
  const fallbackIndex = index % fallbackImages.length
  const fallbackUrl = fallbackImages[fallbackIndex]
  if (await isValidImageUrl(fallbackUrl)) {
    return fallbackUrl
  }
  // If the selected fallback fails, cycle through others
  for (let i = 0; i < fallbackImages.length; i++) {
    const url = fallbackImages[i]
    if (await isValidImageUrl(url)) {
      return url
    }
  }
  // Default to a known working image as a last resort
  return 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c'
}

// Fetch 10 events using Gemini API
export async function fetchEventsFromGemini() {
  const prompt = `
    Generate exactly 10 realistic future events for Factory Hammerbrooklyn (https://factory.network/factory-hammerbrooklyn/),
    a coworking and innovation space in Hamburg. Start from April 07, 2025, and spread the events over the next 4 months.
    Include event types like startup pitches, tech meetups, workshops, and networking events. Keep descriptions concise, 4-5 words max.
    Return the data as a JSON array in this format, with no additional text or code:
    [
      {"title": "Event Name", "description": "Event Description (one sentence)", "date": "Month DD, YYYY", "location": "Factory Hammerbrooklyn, Hamburg"},
      ...
    ]
  `

  const result = await model.generateContent(prompt)
  const rawResponse = result.response.text()
  const parsedEvents = extractJsonFromText(rawResponse)

  // Trim descriptions and ensure location is consistent
  const processedEvents = parsedEvents.slice(0, 10).map(event => ({
    ...event,
    description: trimToOneSentence(event.description),
    location: 'Factory Hammerbrooklyn, Hamburg',
  }))

  // Store the events
  await storeData('factoryEvents', {
    data: processedEvents,
    timestamp: moment().toISOString(),
  })

  // Filter for future events (should all be future given the prompt)
  return processedEvents.filter(event => moment(event.date, 'MMMM DD, YYYY').isSameOrAfter(moment(), 'day'))
}

// Main function to get events (from storage or Gemini)
export async function getEvents() {
  const storedData = await getStoredData('factoryEvents')
  if (storedData && moment(storedData.timestamp).isSame(moment(), 'day')) {
    return storedData.data
  }
  await removeStoredItem('factoryEvents')
  return await fetchEventsFromGemini()
}

// Fetch news/announcement items from mock data
export async function fetchNewsFromGemini() {
  // Use the imported mock data
  const parsedNews = await Promise.all(
    mockInfoItems.slice(0, 8).map(async (item, index) => {
      // Validate and process all image URLs in parallel
      const entityPromises = item.files.entities.map(async (img, imgIndex) => {
        const validatedUrl = await getValidImage(index * 10 + imgIndex, img.url)
        return {
          ...img,
          url: validatedUrl,
        }
      })

      const processedEntities = await Promise.all(entityPromises)

      return {
        ...item,
        id: item.id || generateUUID(),
        version: item.version || generateUUID(),
        files: {
          type: 'files',
          entities: processedEntities,
        },
        shortText: trimToOneSentence(item.shortText),
        creationTime:
          item.creationTime ||
          moment()
            .subtract(Math.floor(Math.random() * 120), 'days')
            .toISOString(),
        lastModified: '2025-04-24T12:00:00Z',
        isNews: index % 2 === 0,
        dummy: true,
      }
    })
  )

  // Store in local cache
  await storeData('factoryNews', {
    data: parsedNews,
    timestamp: moment().toISOString(),
  })

  return parsedNews
}

// Main function to get news (from storage or mock data)
export async function getNews() {
  const storedData = await getStoredData('factoryNews')
  if (storedData && moment(storedData.timestamp).isSame(moment(), 'day')) {
    return storedData.data
  }
  await removeStoredItem('factoryNews')
  return await fetchNewsFromGemini()
}
export async function createGetImageByContext(context = '') {
  if (!context) return await getValidImage(0)

  const key = context.trim().toLowerCase()
  const cache = (await getStoredData('contextImageMap')) || {}

  if (cache[key] && (await isValidImageUrl(cache[key]))) {
    return cache[key]
  }

  try {
    const searchKeyword = encodeURIComponent(context.trim())
    const { unsplashAuth } = await loadConfig()
    const apiUrl = `https://api.unsplash.com/search/photos?query=${searchKeyword}&per_page=5&orientation=landscape&client_id=${unsplashAuth.accessKey}`

    const res = await fetch(apiUrl)
    const data = await res.json()
    const photo = data?.results?.find(p => p?.urls?.regular)

    if (photo && (await isValidImageUrl(photo.urls.regular))) {
      const url = photo.urls.regular
      cache[key] = url
      await storeData('contextImageMap', cache)
      return url
    }
  } catch (e) {
    console.warn('Unsplash image fetch failed:', e)
  }

  const fallback = await getValidImage(0)
  cache[key] = fallback
  await storeData('contextImageMap', cache)
  return fallback
}

export async function getFormattedHtmlFromText(input = '') {
  if (!input?.trim()) return ''

  try {
    const prompt = `
Transform the following plain or bullet-pointed text into professionally styled HTML:
- Use headings, bold, and italic where appropriate.
- Apply inline styles for better visual presentation (e.g., colors, spacing).
- Keep the tone suitable for internal office communication or smart building updates.
- Do not return any explanation or comment — only the raw HTML.

Text:
${input}
`

    const result = await model.generateContent(prompt)
    const rawHtml = result.response.text().trim()

    // Clean up if wrapped in code block
    if (rawHtml.startsWith('```html') && rawHtml.endsWith('```')) {
      return rawHtml.replace(/^```html|```$/g, '').trim()
    }

    return rawHtml
  } catch (error) {
    console.error('Error generating HTML from Gemini:', error)
    return `<p>${input}</p>` // Fallback plain paragraph
  }
}
